// src/utils/unified-credit-config.js
// 🎯 SINGLE SOURCE OF TRUTH for Credit Packages, fanType↔credit mappings, and backend compatibility
// Keeps naming consistent with credit-service and paywall/confirm flows.

// -------------------------------------------------------------
// Public credit packages shown to users (Stripe priceIds included)
// -------------------------------------------------------------
export const UNIFIED_CREDIT_PACKAGES = Object.freeze({
  dedicated: Object.freeze([
    Object.freeze({
      priceId: "price_1Ro3sgFQFtOFh3FhUh7yDGmg",
      credits: 1,
      price: "$0.99",
      amount: 99,
      name: "Single Credit",
      packageType: "single",
      description: "Perfect for trying out Dedicated Fan mode",
    }),
    Object.freeze({
      priceId: "price_1Ro3t4FQFtOFh3Fh9QKhUMqV",
      credits: 5,
      price: "$3.99",
      amount: 399,
      name: "5-Credit Pack",
      packageType: "fivePack",
      description: "Best value for regular fans",
      popular: true,
    }),
    Object.freeze({
      priceId: "price_1Ro3tQFQFtOFh3FhqFh5R8tS",
      credits: 10,
      price: "$6.99",
      amount: 699,
      name: "10-Credit Pack",
      packageType: "tenPack",
      description: "For the most dedicated fans",
    }),
  ]),
  diehard: Object.freeze([
    Object.freeze({
      priceId: "price_1Ro3ttFQFtOFh3FhjXvt1zg8",
      credits: 1,
      price: "$1.99",
      amount: 199,
      name: "Single Credit",
      packageType: "single",
      description: "Try the full DieHard experience",
    }),
    Object.freeze({
      priceId: "price_1Ro3uCFQFtOFh3FhmCevx4UE",
      credits: 3,
      price: "$4.99",
      amount: 499,
      name: "3-Credit Pack",
      packageType: "threePack",
      description: "Great for serious analysis",
      popular: true,
    }),
    Object.freeze({
      priceId: "price_1Ro3uSFQFtOFh3FhjwITc5X6",
      credits: 5,
      price: "$7.99",
      amount: 799,
      name: "5-Credit Pack",
      packageType: "fivePack",
      description: "Ultimate value for die-hard fans",
    }),
  ]),
});

// -------------------------------------------------------------
// Fan type mapping utilities (Frontend ↔ Backend/Service)
// Frontend uses: casual, dedicated, diehard
// Backend/service stores credits under: dedicated, expert
// -------------------------------------------------------------
export const FAN_TYPE_MAPPINGS = Object.freeze({
  // Frontend -> Backend
  toBackend: (fanType) => {
    if (!fanType) return fanType;
    const ft = String(fanType).toLowerCase();
    if (ft === "diehard") return "expert";
    if (ft === "dedicatedfan") return "dedicated";
    if (ft === "expertanalyst") return "expert";
    return ft;
  },

  // Backend -> Frontend
  toFrontend: (fanType) => {
    if (!fanType) return fanType;
    const ft = String(fanType).toLowerCase();
    if (ft === "expert") return "diehard";
    if (ft === "dedicated") return "dedicated";
    return ft;
  },

  // Legacy normalization used by UI
  normalize: (fanType) => {
    if (!fanType) return fanType;
    const ft = String(fanType).toLowerCase();
    switch (ft) {
      case "expert":
      case "expertanalyst":
        return "diehard";
      case "dedicatedfan":
        return "dedicated";
      default:
        return ft;
    }
  },
});

// -------------------------------------------------------------
// Credit type mapping used by service layer (credit-service.js)
// Returns: 'dedicated' | 'expert' | null (for casual)
// -------------------------------------------------------------
export const mapFanTypeToCredit = (fanType) => {
  if (!fanType) return "expert";
  const ft = FAN_TYPE_MAPPINGS.toBackend(fanType); // maps diehard->expert, etc.
  switch (ft) {
    case "casual":
      return null;
    case "dedicated":
      return "dedicated";
    case "expert":
      return "expert";
    default:
      // keep lightweight here to avoid pulling logger into utils
      if (typeof console !== "undefined") {
        console.warn("[unified-credit-config] Unknown fanType:", fanType, "→ defaulting to 'expert'");
      }
      return "expert";
  }
};

// -------------------------------------------------------------
// Backend compatibility map (priceId → { fanType, credits, ...})
// Ensures functions/checkout handlers can resolve a price to
// the correct credit bucket ('expert' for diehard).
// -------------------------------------------------------------
export const BACKEND_CREDIT_CONFIG = (() => {
  const config = {};
  Object.entries(UNIFIED_CREDIT_PACKAGES).forEach(([frontendType, packages]) => {
    packages.forEach((pkg) => {
      const backendType = FAN_TYPE_MAPPINGS.toBackend(frontendType);
      config[pkg.priceId] = {
        fanType: backendType, // 'dedicated' or 'expert'
        credits: pkg.credits,
        packageType: pkg.packageType,
        amount: pkg.amount,
        name: pkg.name,
      };
    });
  });
  return Object.freeze(config);
})();

// -------------------------------------------------------------
// Small helpers for consumers (UI/paywall/functions)
// -------------------------------------------------------------
export const getPackagesFor = (fanType) => {
  const normalized = FAN_TYPE_MAPPINGS.normalize(fanType);
  return UNIFIED_CREDIT_PACKAGES[normalized] || [];
};

export const getPriceConfig = (priceId) => BACKEND_CREDIT_CONFIG[priceId] || null;

export const DEFAULT_PACKAGE_FOR = Object.freeze({
  dedicated: UNIFIED_CREDIT_PACKAGES.dedicated[1], // 5-pack as default
  diehard: UNIFIED_CREDIT_PACKAGES.diehard[1], // 3-pack as default
});
